import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import useModal from 'hooks/useModal';
import { ProviderProps, useCallback, useContext, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import createNamedContext from 'utils/createNamedContext';
import ScenarioModal, { ModalMeta } from './ScenarioModal';

export function useGetScenarioText() {
  const { t } = useTranslate();

  const systemLang = useSystemLanguageValue();

  const botReplyTypeLabel = useMemo<
    Omit<
      Record<Conversation.BotReplyType, string>,
      'BRT_UNKNOWN' | 'MARKETING' | 'LOFA_MARKETING' | 'RECEPTION_SCORE'
    >
  >(() => {
    return {
      DIALOGUE: t('aide.AI智能接待'),
      WELCOME: t('aide.进店欢迎语'),
      TIMEOUT_AUTO_REPLY: t('nav.autoReply'),
      GUARANTEED_ANSWER:
        (
          {
            'zh-CN': 'AI值守兜底',
            'en-US': 'AI On-duty reply',
          } as Partial<Record<General.SystemLanguage, string>>
        )[systemLang] || `${t('aide.AI智能接待')}-${t('aide.值守兜底')}`,
      COMPENSATORY_REPLY:
        (
          {
            'zh-CN': ' AI结束语',
            'en-US': 'Closing words by AI',
          } as Partial<Record<General.SystemLanguage, string>>
        )[systemLang] || `${t('aide.AI智能接待')}-${t('aide.结束语')}`,
      TO_BUYER_AUTO_REPLY: t('aide.无用消息的自动回复'),
      TO_BLOCK_BUYER: t('aide.黑名单自动回复'),
      LLM_AGENT: t('AI智能接待Pro'),
    };
  }, [systemLang, t]);

  const botMarketingTypeLabel = useMemo<
    Omit<Record<Conversation.BotMarketingType, string>, 'BMT_UNKNOWN'>
  >(() => {
    return {
      BMT_INVITE_FOLLOWER: t('auto_invite_follow'),
      BMT_ORDER_REMINDER: t('aide.催下单'),
      BMT_PAYMENT_REMINDER: t('marketing.催付款'),
      BMT_PRAISE_REMINDER: t('marketing.催好评'),
      BMT_BAD_REVIEW_RECOVERY: t('aide.中差评挽回'),
      BMT_CANCEL_ORDER_REMINDER: t('aide.买家取消订单挽回'),
      BMT_SYSTEM_CANCEL_ORDER: t('aide.系统取消订单挽回'),
      BMT_RETURN_ORDER_REMINDER: t('aide.newScene.RETURN_REFUND_RETRIEVE'),
      BMT_ONLY_RETURN_ORDER_REMINDER: t('aide.newScene.REFUND_RETRIEVE'),
      BMT_RETURN_TRADE_REMINDER: t('aide.仅退款/退货退款'),
      BMT_OUT_STOCK_REMINDER: t('aide.缺货提醒'),
      BMT_CONFIRM_ORDER_REMINDER: `${t('aide.物流呵护')}-${t('aide.订单确认')}`,
      BMT_DELAY_SHIP: `${t('aide.物流呵护')}-${t('aide.延迟发货')}`,
      BMT_SHIPPED_REMINDER: `${t('aide.物流呵护')}-${t('aide.已发货')}`,
      BMT_WAIT_FOR_TAX: `${t('aide.物流呵护')}-${t('aide.等待缴税')}`,
      BMT_DELIVERY_REMINDER: `${t('aide.物流呵护')}-${t('aide.尾程派送')}`,
      BMT_WAIT_DELIVERY_AGAIN: `${t('aide.物流呵护')}-${t('aide.等待再次派送')}`,
      BMT_SIGN_REMINDER: `${t('aide.物流呵护')}-${t('aide.已签收')}`,
      BMT_GOOD_REVIEW_REMINDER: t('aide.好评关怀'),
      BMT_SIGNAL_COUPON: t('aide.暗号领券'),
      BMT_ORDER_FOLLOWUP: t('问题订单跟进'),
      BMT_INITIATIVE_TASK: `${t('AI促销机器人')}-${t('主动联系买家')}`,
    };
  }, [t]);

  const getScenarioText = useCallback(
    (botReplyType: Conversation.BotReplyType, extraInfo?: Conversation.ExtraInfo) => {
      if (botReplyType === 'BRT_UNKNOWN') {
        return undefined;
      }

      const { botMarketingType, replyOption } = extraInfo || {};

      if (botReplyType === 'MARKETING') {
        if (!botMarketingType || botMarketingType === 'BMT_UNKNOWN') {
          return undefined;
        }

        return botMarketingTypeLabel[botMarketingType];
      }

      if (botReplyType === 'RECEPTION_SCORE') {
        if (replyOption) {
          return t('aide.对买家评分的回复');
        }

        return t('aide.接待评分邀请');
      }

      return botReplyTypeLabel[botReplyType];
    },
    [botReplyTypeLabel, botMarketingTypeLabel, t],
  );

  return {
    t,
    botReplyTypeLabel,
    botMarketingTypeLabel,
    getScenarioText,
  };
}

export function useScenarioContextValue() {
  const modal = useModal<ModalMeta>();

  return {
    modal,
    ...useGetScenarioText(),
  };
}

export type ScenarioContextValue = ReturnType<typeof useScenarioContextValue> | null;

export const ScenarioContext = createNamedContext<ScenarioContextValue>('ScenarioContext', null);

export const useScenarioContext = () => {
  const value = useContext(ScenarioContext);

  if (value === null) {
    throw new Error(
      'useScenarioContext hook should be used in a context of ScenarioProvider component',
    );
  }

  return value;
};

export function ScenarioProvider(props: Omit<ProviderProps<ScenarioContextValue>, 'value'>) {
  const { children, ...otherProps } = props;

  const value = useScenarioContextValue();

  const {
    modal: { visible, hide, meta },
  } = value;

  return (
    <ScenarioContext.Provider value={value} {...otherProps}>
      {children}
      {meta && <ScenarioModal {...meta} visible={visible} onCancel={() => hide()} />}
    </ScenarioContext.Provider>
  );
}

// 获取下发场景文本
export function useGetBotReplyTypeText() {
  const { getScenarioText } = useGetScenarioText();

  return (botReplyType: Conversation.BotReplyType, extra: string) => {
    try {
      const extraInfo = JSON.parse(extra);

      return getScenarioText(botReplyType, extraInfo) || '-';
    } catch (err) {
      return '-';
    }
  };
}
